import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getLatestEthogram,
  listSubjects,
  submitObservation,
  toTerms,
  type Ethogram,
  type Subject,
} from '../../lib/api/endpoints.js';
import { ACCEPTED_TYPES } from '../../lib/audio/prepare.js';
import { assessTake } from '../../lib/audio/assess.js';
import { TakeCard } from '../../components/TakeCard.js';
import { TermPicker } from '../../components/TermPicker.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { Select } from '../../components/Select.js';
import { RecordPanel } from '../../components/RecordPanel.js';
import { SubjectPicker } from '../../components/SubjectPicker.js';
import { useUpload } from './useUpload.js';
import { buildRecord, emptyAnswers, isSubmittable, type AbcAnswers } from './buildRecord.js';
import {
  computeCreditRules,
  ordinal,
  totalCredits,
  useObservationDigest,
  type ObservationDigest,
} from '../../lib/standing.js';
import type { Contributor } from '../../lib/api/endpoints.js';

interface Props {
  contributor: Contributor;
  /** Null until the server has confirmed it; Submit stays disabled until then. */
  licence: string | null;
  onContributed: (observationId: string) => void;
}

/** The Q&A half of the flow, once there's audio to ask about. */
const STEPS = ['antecedent', 'signal', 'consequence', 'review'] as const;
type Step = (typeof STEPS)[number];

interface JustSubmitted {
  id: string;
  subjectLabel: string;
  signalLabel: string;
  creditsEarned: number;
  /** Null only if the digest hadn't loaded yet — never a guess. */
  creditsTotal: number | null;
}

function orderByLastObserved(subjects: Subject[], digest: ObservationDigest | null): Subject[] {
  if (!digest) return subjects;
  return [...subjects].sort((a, b) => {
    const aAt = digest.lastObservedAtBySubject[a.id];
    const bAt = digest.lastObservedAtBySubject[b.id];
    if (aAt && bAt) return bAt.localeCompare(aAt);
    if (aAt) return -1;
    if (bAt) return 1;
    return 0;
  });
}

/**
 * The contribution flow.
 *
 * The design target from the implementation plan: **a median contribution
 * under 45 seconds**, tracked as a first-class product metric. If it climbs,
 * contributions collapse — a distressed pet owner will not fill in forty
 * fields, and a corpus that demands forty fields gets no records at all.
 *
 * Four things buy that target:
 *
 * 1. **The animal is chosen once, at the top, without leaving the flow.** The
 *    picker orders by most recently recorded, so the usual animal is the
 *    first tap — one tap from app-open to recording when it's the usual one,
 *    two when it isn't.
 * 2. **The upload runs during the questions** (see useUpload). Perceived time
 *    is the questions only, not questions plus transfer.
 * 3. **Three required answers.** Trigger, signal, what happened next. Depth is
 *    real but optional, and lives behind a disclosure the impatient never open.
 * 4. **Chips, not dropdowns.** Selecting from visible options is faster than
 *    opening a select, and it lets the vocabulary teach itself.
 *
 * The A/B/C questions are asked **one at a time**, not as a single long
 * scroll — each is a distinct moment, not a section of a form. It also means
 * every question can address the animal by name rather than "the recording."
 *
 * The colour split — teal for A and C, amber for B — is the project's thesis
 * rendered rather than explained. The teal fields are the ones no other corpus
 * keeps.
 */
export function ContributePage({ contributor, licence, onContributed }: Props) {
  const [subjects, setSubjects] = useState<Subject[] | null>(null);
  const { digest, refresh: refreshDigest } = useObservationDigest();
  const [ethogram, setEthogram] = useState<Ethogram | null>(null);
  const [answers, setAnswers] = useState<AbcAnswers>(() => emptyAnswers(''));
  const [error, setError] = useState<unknown>(null);
  // Set only for load failures, where "try again" means re-fetching; a failed
  // submission is retried by pressing Submit again.
  const [errorRetry, setErrorRetry] = useState<(() => void) | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [justSubmitted, setJustSubmitted] = useState<JustSubmitted | null>(null);
  // Record is the default: it's the point of a phone in your hand when an
  // animal is mid-signal, and it's the capability the file input alone can't
  // offer. Someone with an existing recording switches in one tap, from
  // inside the record card itself.
  const [mode, setMode] = useState<'record' | 'file'>('record');

  const { phase, audio, start, retry, reset } = useUpload();

  const retryLoad = () => {
    setError(null);
    setLoadAttempt((n) => n + 1);
  };
  const fileInput = useRef<HTMLInputElement>(null);

  // The take is a full spectrogram card right after recording, then tucks into
  // a compact strip once the contributor starts on the questions — the strip
  // rides along with them, the picture is one tap away. `pinned` means they
  // asked to see it: after that it stays open, never collapsing back on them.
  const [takeExpanded, setTakeExpanded] = useState(true);
  const takePinned = useRef(false);
  const resetTakeView = () => {
    setTakeExpanded(true);
    takePinned.current = false;
  };
  const toggleTakeExpanded = () => {
    setTakeExpanded((open) => {
      takePinned.current = !open;
      return !open;
    });
  };
  // Answering is what "starting the questions" means. Deliberately on click and
  // keydown, not pointer-down: collapsing shifts the layout, and a shift between
  // pointer-down and pointer-up would swallow the very tap that triggered it.
  const tuckTakeAway = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (takePinned.current) return;
    if ('key' in e && (e.key === 'Tab' || e.key === 'Shift')) return;
    setTakeExpanded(false);
  };

  // True once the contributor has explicitly picked an animal themselves —
  // after that, arriving standing data must never silently steal the
  // selection out from under them.
  const manualPick = useRef(false);

  // Time-to-contribute, measured from the moment a file is chosen. The plan
  // names this as a metric to watch from day one; measuring it locally is the
  // cheapest possible version of that.
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    listSubjects()
      .then((list) => {
        setSubjects(list);
        if (list.length > 0) {
          setAnswers((a) => (a.subjectId ? a : { ...a, subjectId: list[0]!.id }));
        }
      })
      .catch((e) => {
        setError(e);
        setErrorRetry(() => retryLoad);
      });
  }, [loadAttempt]);

  // Once standing data arrives, prefer the animal actually recorded most
  // recently over the arbitrary first-in-list default — but only while
  // nothing is in flight and the contributor hasn't picked for themselves.
  useEffect(() => {
    if (manualPick.current || !digest || !subjects || subjects.length === 0) return;
    if (phase.status !== 'idle') return;
    const preferred = orderByLastObserved(subjects, digest)[0]!.id;
    setAnswers((a) => (a.subjectId === preferred ? a : { ...a, subjectId: preferred }));
  }, [digest, subjects, phase.status]);

  const subject = useMemo(
    () => subjects?.find((s) => s.id === answers.subjectId) ?? null,
    [subjects, answers.subjectId],
  );

  // Used to address every question to the actual animal instead of "the
  // recording" — the whole point of the animal existing as a named
  // individual in the data model in the first place.
  const subjectLabel = subject?.name ?? subject?.commonName ?? subject?.scientificName ?? 'your animal';

  // A poor-but-usable take is flagged, never refused — useUpload already
  // stopped the empty ones. Memoised because it walks every decoded sample.
  const takeWarning = useMemo(() => {
    if (!audio) return null;
    const assessment = assessTake(audio);
    return assessment.severity === 'warn' ? assessment.message : null;
  }, [audio]);

  const standingLabel = useMemo(() => {
    if (!subject || !digest) return undefined;
    const nth = (digest.bySubject[subject.id] ?? 0) + 1;
    return `${subjectLabel}'s ${ordinal(nth)} recording · +10 credits when it lands`;
  }, [subject, digest, subjectLabel]);

  // The vocabulary is fetched per species and cached by version. Never
  // hardcoded — that is the whole point of serving it.
  useEffect(() => {
    if (!subject) return;
    let cancelled = false;
    getLatestEthogram(subject.scientificName)
      .then((e) => {
        if (!cancelled) setEthogram(e);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        setErrorRetry(() => retryLoad);
      });
    return () => {
      cancelled = true;
    };
  }, [subject, loadAttempt]);

  const onFile = (file: File | undefined) => {
    if (!file) return;
    resetTakeView();
    setError(null);
    setStepIndex(0);
    startedAt.current = performance.now();
    void start(file);
  };

  const chooseAnother = () => {
    reset();
    resetTakeView();
    setStepIndex(0);
  };

  const changeSubject = (id: string) => {
    if (id === answers.subjectId) return;

    if (phase.status !== 'idle') {
      const ok = window.confirm(
        'Switch animals? The current recording is about a different one, and will be discarded.',
      );
      if (!ok) return;
      reset();
    }

    manualPick.current = true;
    setAnswers(emptyAnswers(id));
    setStepIndex(0);
    setError(null);
  };

  const submit = async () => {
    if (!subject || !ethogram || !audio || phase.status !== 'done' || licence === null) return;

    setSubmitting(true);
    setError(null);

    try {
      const record = buildRecord({
        answers,
        subject,
        audio,
        assetId: phase.assetId,
        ethogram,
        contributorId: contributor.id,
        contributorRole: contributor.defaultRole,
        licence,
      });

      const created = await submitObservation(record);

      if (startedAt.current !== null) {
        const seconds = ((performance.now() - startedAt.current) / 1000).toFixed(1);

        console.info(`[voxfauna] time-to-contribute: ${seconds}s`);
      }

      // A specific, true acknowledgment — the animal's name and the signal
      // just logged, nothing invented (no fabricated "3rd this month" style
      // count: that would need a real query, and a guess dressed as a stat is
      // exactly the kind of thing this project elsewhere refuses to show).
      const signalLabel =
        toTerms(ethogram.signal_types).find((t) => t.term === answers.signalType)?.label ??
        answers.signalType;

      // Same two rules computeCreditRules charges everyone: +10 for landing,
      // +5 more if the antecedent was filled in too — the only optional field
      // isFullContext (lib/standing.ts) actually checks, since signal and
      // consequence are already required. Computed here, once, against the
      // digest as it stood before this submission, so the total shown can't
      // drift from a since-changed answers or a since-refreshed digest.
      const earnedFullContext =
        answers.antecedentTerms.length > 0 || answers.antecedentFree.trim() !== '';
      const creditsEarned = 10 + (earnedFullContext ? 5 : 0);
      const priorCredits = digest ? totalCredits(computeCreditRules(digest)) : null;
      const creditsTotal = priorCredits !== null ? priorCredits + creditsEarned : null;

      setJustSubmitted({ id: created.id, subjectLabel, signalLabel, creditsEarned, creditsTotal });
      // The digest is now one observation stale — refreshed so the credits
      // badge, the next standing line and the picker ordering all catch up.
      refreshDigest();
    } catch (e) {
      setError(e);
      setErrorRetry(null);
    } finally {
      setSubmitting(false);
    }
  };

  const addAnother = () => {
    if (subject) setAnswers(emptyAnswers(subject.id));
    setJustSubmitted(null);
    setStepIndex(0);
    reset();
    // The digest is now one observation stale — refreshed so the next
    // standing line and picker ordering reflect what was just logged.
    refreshDigest();
  };

  const viewTimeline = () => {
    if (justSubmitted) onContributed(justSubmitted.id);
  };

  if (subjects === null) return <p className="meta">Loading…</p>;

  if (subjects.length === 0) {
    return (
      <div className="empty">
        <h2>First, who are we listening to?</h2>
        <p>
          Observations belong to an individual animal, not a species. That is what makes a timeline
          possible — and what lets researchers treat repeated recordings of your dog as repeated
          measures rather than as unrelated samples.
        </p>
        <a className="btn" href="#/animals">
          Add an animal
        </a>
      </div>
    );
  }

  if (justSubmitted) {
    return (
      <div>
        <p className="eyebrow">Contribute</p>
        <section className="panel confirm">
          <h2>Logged for {justSubmitted.subjectLabel}.</h2>
          <p>
            That {justSubmitted.signalLabel.toLowerCase()} is now part of {justSubmitted.subjectLabel}
            's timeline.
          </p>
          {justSubmitted.creditsTotal !== null && (
            <p className="confirm-credits">
              +{justSubmitted.creditsEarned} credits · <strong>{justSubmitted.creditsTotal}</strong>{' '}
              total · <a href="#/record">see the breakdown</a>
            </p>
          )}
          <div className="stack" style={{ justifyContent: 'center' }}>
            <button type="button" className="btn" onClick={addAnother}>
              Record another for {justSubmitted.subjectLabel}
            </button>
            <button type="button" className="btn btn--quiet" onClick={viewTimeline}>
              View {justSubmitted.subjectLabel}'s timeline
            </button>
          </div>
        </section>
      </div>
    );
  }

  const update = <K extends keyof AbcAnswers>(key: K, value: AbcAnswers[K]) =>
    setAnswers((a) => ({ ...a, [key]: value }));

  const ready = phase.status === 'done' && isSubmittable(answers) && !submitting && licence !== null;
  const step: Step = STEPS[stepIndex]!;

  const uploadStatus =
    phase.status === 'uploading' ? (
      <>
        <div className="progress">
          <div className="progress-bar" style={{ width: `${Math.round(phase.progress * 100)}%` }} />
        </div>
        <p className="meta" style={{ marginTop: '0.4rem' }}>
          Uploading in the background — keep going, it will be ready before you are.
        </p>
      </>
    ) : phase.status === 'done' ? (
      <p className="meta">Upload complete.</p>
    ) : phase.status === 'failed' ? (
      <div className="notice notice--error" style={{ marginBottom: 0 }}>
        {phase.message}{' '}
        <button type="button" className="btn btn--quiet" onClick={retry}>
          Try again
        </button>
      </div>
    ) : null;

  return (
    <div>
      <p className="eyebrow">Contribute</p>
      <h1 className="page-title">What did you hear?</h1>
      <p className="page-intro">
        Three questions. The middle one is what every corpus already records; the other two are the
        reason this one is worth building.
      </p>

      {error !== null && <ErrorNotice error={error} onRetry={errorRetry ?? undefined} />}

      <SubjectPicker
        subjects={subjects}
        subjectId={answers.subjectId}
        lastObservedAtBySubject={digest?.lastObservedAtBySubject ?? {}}
        onChange={changeSubject}
      />

      {/* ── The recording ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        {mode === 'record' && phase.status === 'idle' && (
          <RecordPanel
            onRecorded={onFile}
            subjectLabel={subjectLabel}
            standingLabel={standingLabel}
            onSwitchToFile={() => setMode('file')}
          />
        )}

        {mode === 'file' && phase.status === 'idle' && (
          <div className="panel">
            <label className="field-label" htmlFor="audio-file">
              The recording
            </label>
            <input
              id="audio-file"
              ref={fileInput}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <p className="field-hint">
              WAV or FLAC if you have them — lossless audio is what makes this corpus useful for
              acoustic analysis later. MP3 and M4A are accepted.
            </p>
            <button
              type="button"
              className="take-idle-alt"
              style={{ marginTop: '0.75rem' }}
              onClick={() => setMode('record')}
            >
              Or record instead
            </button>
          </div>
        )}

        {phase.status === 'unusable' && (
          <div className="take-done">
            <h2 className="take-done-title">Nothing came through on that one</h2>
            <p style={{ margin: '0 0 0.9rem', color: 'var(--ink-2)' }}>{phase.message}</p>
            <button type="button" className="take-done-again" onClick={chooseAnother}>
              {mode === 'record' ? 'Record again' : 'Choose another'}
            </button>
          </div>
        )}

        {phase.status !== 'idle' && phase.status !== 'unusable' && (
          audio ? (
            <TakeCard
              audio={audio}
              expanded={takeExpanded}
              onToggleExpanded={toggleTakeExpanded}
              recordAgainLabel={mode === 'record' ? 'Record again' : 'Choose another'}
              onRecordAgain={chooseAnother}
              warning={takeWarning}
            >
              {uploadStatus}
            </TakeCard>
          ) : (
            <div className="take-done">
              <p className="meta">Reading the file…</p>
              {uploadStatus}
            </div>
          )
        )}
      </div>

      {/* Everything below only appears once there is audio — an empty A–B–C
          form with no recording is intimidating and pointless. */}
      {audio && ethogram && (
        <div onClickCapture={tuckTakeAway} onKeyDownCapture={tuckTakeAway}>
          <div className="stepper">
            <div className="stepper-row">
              <span>
                Step {stepIndex + 1} of {STEPS.length}
              </span>
              <span>{STEP_TITLES[step]}</span>
            </div>
            <div className="progress">
              <div
                className="progress-bar"
                style={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }}
              />
            </div>
          </div>

          {/* ── A ──────────────────────────────────────────────────────── */}
          {step === 'antecedent' && (
            <section className="abc-step abc-step--context">
              <p className="abc-key">A — Antecedent</p>
              <h2 className="abc-question" id="q-antecedent">
                What happened just before {subjectLabel} did this?
              </h2>
              <p className="abc-aside">
                Almost no corpus records this. It is most of what makes the sound mean anything.
              </p>
              <TermPicker
                terms={toTerms(ethogram.antecedent_terms)}
                selected={answers.antecedentTerms}
                onChange={(t) => update('antecedentTerms', t)}
                tone="context"
                labelledBy="q-antecedent"
              />
              <input
                type="text"
                placeholder="Or describe it — “the delivery van pulled in”"
                value={answers.antecedentFree}
                onChange={(e) => update('antecedentFree', e.target.value)}
                style={{ marginTop: '0.7rem' }}
              />

              <div className="stack" style={{ justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="button" className="btn" onClick={() => setStepIndex(1)}>
                  Next — the signal itself
                </button>
              </div>
            </section>
          )}

          {/* ── B ──────────────────────────────────────────────────────── */}
          {step === 'signal' && (
            <section className="abc-step abc-step--signal">
              <p className="abc-key">B — Signal</p>
              <h2 className="abc-question" id="q-signal">
                What did you hear?
              </h2>
              <p className="abc-aside">
                Describe the sound, not what you think it meant — the meaning goes further down, and
                keeping them apart is what lets anyone check the interpretation later.
              </p>
              <TermPicker
                terms={toTerms(ethogram.signal_types)}
                selected={answers.signalType ? [answers.signalType] : []}
                onChange={(t) => update('signalType', t[0] ?? '')}
                tone="signal"
                single
                labelledBy="q-signal"
              />

              <div className="field" style={{ marginTop: '1rem', marginBottom: 0 }}>
                <span className="field-label" id="intensity-label">
                  How intense — {answers.intensity} of 5
                </span>
                {/* Five buttons, not a native range: a range's thumb is a poor touch
                    target and doesn't read as an instrument. Each bar is a full
                    44×64 tap target regardless of its own (purely visual) height. */}
                <div className="intensity" role="group" aria-labelledby="intensity-label">
                  {([1, 2, 3, 4, 5] as const).map((level) => {
                    const filled = level <= answers.intensity;
                    const current = level === answers.intensity;
                    return (
                      <button
                        key={level}
                        type="button"
                        aria-pressed={filled}
                        aria-label={`Intensity ${level} of 5${current ? ', current' : ''}`}
                        className={`intensity-bar${current ? ' intensity-bar--current' : ''}`}
                        onClick={() => update('intensity', level)}
                      >
                        {filled && (
                          <span
                            className="intensity-bar-fill"
                            style={{ height: `${(level / 5) * 100}%` }}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="stack" style={{ justifyContent: 'space-between', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn--quiet" onClick={() => setStepIndex(0)}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!answers.signalType}
                  onClick={() => setStepIndex(2)}
                >
                  Next — what {subjectLabel} did after
                </button>
              </div>
            </section>
          )}

          {/* ── C ──────────────────────────────────────────────────────── */}
          {step === 'consequence' && (
            <section className="abc-step abc-step--context">
              <p className="abc-key">C — Consequence</p>
              <h2 className="abc-question" id="q-consequence">
                What did {subjectLabel} do next?
              </h2>
              <p className="abc-aside">
                This is the field that turns a recording into evidence about what the signal was
                <em> for</em>.
              </p>
              <TermPicker
                terms={toTerms(ethogram.consequence_terms)}
                selected={answers.consequenceTerms}
                onChange={(t) => update('consequenceTerms', t)}
                tone="context"
                labelledBy="q-consequence"
              />
              <input
                type="text"
                placeholder="In your words — “ran to the door and kept barking”"
                value={answers.nextAction}
                onChange={(e) => update('nextAction', e.target.value)}
                style={{ marginTop: '0.7rem' }}
                aria-describedby="next-action-hint"
              />
              <p className="field-hint" id="next-action-hint">
                Required.
              </p>

              <div className="stack" style={{ justifyContent: 'space-between', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn--quiet" onClick={() => setStepIndex(1)}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!answers.nextAction.trim()}
                  onClick={() => setStepIndex(3)}
                >
                  Review &amp; finish
                </button>
              </div>
            </section>
          )}

          {/* ── Review ─────────────────────────────────────────────────── */}
          {step === 'review' && (
            <>
              <details className="panel" style={{ marginBottom: '2rem' }}>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontFamily: 'var(--mono)',
                    fontSize: '0.78rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--ink-2)',
                  }}
                >
                  Add more detail — optional, and you can do it later
                </summary>

                <div style={{ marginTop: '1.25rem' }}>
                  <div className="field">
                    <span className="field-label" id="q-body">
                      Body language you noticed
                    </span>
                    <TermPicker
                      terms={toTerms(ethogram.body_language)}
                      selected={answers.bodyLanguage}
                      onChange={(t) => update('bodyLanguage', t)}
                      tone="context"
                      labelledBy="q-body"
                    />
                  </div>

                  <div className="field">
                    <label className="field-label" htmlFor="meaning">
                      What do you think {subjectLabel} meant?
                    </label>
                    <input
                      id="meaning"
                      type="text"
                      placeholder="“Someone's here, come and look”"
                      value={answers.proposedMeaning}
                      onChange={(e) => update('proposedMeaning', e.target.value)}
                    />
                    <p className="field-hint">
                      Recorded as your hypothesis, never as fact. Researchers need to know what the
                      person who was there believed — and to be able to disagree with it.
                    </p>
                  </div>

                  <div className="field">
                    <span className="field-label" id="q-meaning-cat">
                      Closest category
                    </span>
                    <TermPicker
                      terms={toTerms(ethogram.meaning_categories)}
                      selected={answers.meaningCategory ? [answers.meaningCategory] : []}
                      onChange={(t) => update('meaningCategory', t[0] ?? '')}
                      tone="context"
                      single
                      labelledBy="q-meaning-cat"
                    />
                  </div>

                  <div className="field">
                    <label className="field-label" htmlFor="certainty">
                      How sure are you?
                    </label>
                    <Select
                      id="certainty"
                      value={answers.certainty}
                      onChange={(value) => update('certainty', value as AbcAnswers['certainty'])}
                      options={[
                        { value: 'high', label: "I've seen this many times" },
                        { value: 'medium', label: 'Fairly sure' },
                        { value: 'guess', label: 'Guessing' },
                      ]}
                      maxWidth="16rem"
                    />
                  </div>

                  <div className="field" style={{ marginBottom: 0 }}>
                    <label className="field-label" htmlFor="resolved">
                      Did the situation resolve?
                    </label>
                    <Select
                      id="resolved"
                      value={answers.didResolve === null ? '' : String(answers.didResolve)}
                      onChange={(value) =>
                        update('didResolve', value === '' ? null : value === 'true')
                      }
                      options={[
                        { value: '', label: 'Not sure' },
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                      ]}
                      maxWidth="16rem"
                    />
                  </div>
                </div>
              </details>

              {/* ── Ethics ─────────────────────────────────────────────── */}
              <section className="panel" style={{ marginBottom: '2rem' }}>
                <p className="eyebrow" style={{ marginBottom: '1rem' }}>
                  Before you submit
                </p>

                <label className="stack" style={{ alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <input
                    type="checkbox"
                    checked={answers.welfareAttested}
                    onChange={(e) => update('welfareAttested', e.target.checked)}
                    style={{ marginTop: '0.3rem' }}
                  />
                  <span style={{ flex: 1, minWidth: '18rem' }}>
                    This happened on its own. I did nothing to provoke or distress {subjectLabel} to
                    produce this recording.
                  </span>
                </label>

                <label className="stack" style={{ alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={answers.humanVoicesPresent}
                    onChange={(e) => update('humanVoicesPresent', e.target.checked)}
                    style={{ marginTop: '0.3rem' }}
                  />
                  <span style={{ flex: 1, minWidth: '18rem' }}>
                    A person can be heard in this recording
                  </span>
                </label>

                {answers.humanVoicesPresent && (
                  <label
                    className="stack"
                    style={{ alignItems: 'flex-start', marginLeft: '1.6rem', marginTop: '0.5rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={answers.humanAudioConsent}
                      onChange={(e) => update('humanAudioConsent', e.target.checked)}
                      style={{ marginTop: '0.3rem' }}
                    />
                    <span style={{ flex: 1, minWidth: '16rem' }}>
                      Everyone audible has agreed to it being contributed.
                      <span className="field-hint" style={{ display: 'block' }}>
                        Required. Someone captured by accident hasn't agreed to anything.
                      </span>
                    </span>
                  </label>
                )}
              </section>

              <div className="stack" style={{ justifyContent: 'space-between' }}>
                <button type="button" className="btn btn--quiet" onClick={() => setStepIndex(2)}>
                  Back
                </button>
                <div className="stack">
                  <button type="button" className="btn" disabled={!ready} onClick={submit}>
                    {submitting
                      ? 'Submitting…'
                      : phase.status === 'uploading'
                        ? 'Waiting for the upload…'
                        : `Add to ${subjectLabel}'s timeline`}
                  </button>
                  <span className="meta">Released under {licence ?? 'the corpus licence'}</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const STEP_TITLES: Record<Step, string> = {
  antecedent: 'Antecedent',
  signal: 'Signal',
  consequence: 'Consequence',
  review: 'Review',
};
